import React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import "./Course.css";
import "./ManageCourses.css";
import "../../App.css";
import SingleCourseElementManage from "../../components/SingleCourseElementManage";
import InfinityCourseElementManage from "../../components/InfinityCourseElementManage";
import {
  dateToFullStr,
  firebaseDateToDateObj,
  objectToArray,
  priceToStr,
} from "../../functions/utils";
import { fetchRetry } from "../../functions/request";
import { MainContext } from "../../contexts/MainContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TestMode from "../../components/TestMode";
import Title from "../../components/Title";
import BlockCourseElementManage from "../../components/BlockCourseElementManage";
import SearchInput from "../../components/SearchInput";
import TextCheckbox from "../../components/TextCheckbox";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import Tag from "../../components/Tag";
import SubscriptionElementManage from "../../components/SubscriptionElementManage";
import InfiniteScroll from "react-infinite-scroll-component";
import CourseElementSkeleton from "../../components/CourseElementSkeleton";
import debounce from "lodash.debounce";
import { apiRequest } from "../../functions/requestMulti";
import {
  ListCacheEntry,
  getScrollContainer,
  restoreListState,
  restoreScrollPosition,
  saveListState,
} from "../../functions/listStateCache";

type Props = {};
type ComposedProps = Props &
  RouteComponentProps<{
    clubId: string;
    branchId: string;
  }>;

export default class ManageCourses extends React.Component<
  ComposedProps,
  {
    clubId: string;
    branchId: string;
    branchLoaded: boolean;
    elementsLoaded: boolean;
    branch: any;
    elements: Array<any>;
    courseSearch: string;
    showDeleted: boolean;
    showPast: boolean;
    selectedTag: string;
    page: number;
    hasMore: boolean;
    batchSize: number;
    initialLoad: boolean;
  }
> {
  static contextType = MainContext;

  private cacheKey: string;
  private restored: ListCacheEntry | null;
  private leavingToDetail = false;
  private cancelScroll: (() => void) | null = null;
  private mounted = false;
  private restoringPosition = false;
  private rootRef = React.createRef<HTMLDivElement>();

  constructor(props: ComposedProps) {
    super(props);
    this.cacheKey = props.match.url;
    this.restored = restoreListState(this.cacheKey);
    this.state = {
      clubId: this.props.match.params.clubId,
      branchId: this.props.match.params.branchId,
      branchLoaded: false,
      elementsLoaded: false,
      branch: {},
      elements: [],
      courseSearch: "",
      showDeleted: false,
      showPast: false,
      selectedTag: "",
      page: 0,
      hasMore: true,
      batchSize: 10,
      initialLoad: false,
      // Returning from a course's detail page: render every page the user had
      // already loaded, plus their search, tags and checkboxes. initialLoad is
      // restored as true so a later "Mehr laden" cannot re-trigger the hash
      // jump on top of the restored scroll position.
      ...(this.restored ? this.restored.state : {}),
    };
  }

  componentDidMount = async () => {
    this.mounted = true;
    if (this.restored) {
      this.restorePosition();
      // Silent refresh of the whole loaded window - elementsLoaded stays true,
      // so no skeletons and no scroll jump.
      this.revalidateWindow();
      this.requestBranch();
      return;
    }
    await Promise.all([this.requestBranch(), this.requestElements()]);
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (this.cancelScroll) this.cancelScroll();
    this.debounceRequest.cancel();
    if (this.leavingToDetail) {
      saveListState(
        this.cacheKey,
        {
          elements: this.state.elements,
          page: this.state.page,
          hasMore: this.state.hasMore,
          courseSearch: this.state.courseSearch,
          showDeleted: this.state.showDeleted,
          showPast: this.state.showPast,
          selectedTag: this.state.selectedTag,
          branch: this.state.branch,
          elementsLoaded: true,
          branchLoaded: true,
          initialLoad: true,
        },
        this.rootRef.current
      );
    }
  };

  /**
   * A back link may carry a `#courseId`. When it names a card that is actually
   * on screen, honour it - it is more precise than the saved offset. Otherwise
   * fall back to the saved scroll position.
   */
  restorePosition = () => {
    this.restoringPosition = true;
    const settled = () => {
      this.restoringPosition = false;
    };
    const hashId = window.location.hash.slice(1);
    const target = hashId ? document.getElementById(hashId) : null;
    if (target) {
      this.scrollToCard(target);
      requestAnimationFrame(settled);
      return;
    }
    if (!this.restored) {
      settled();
      return;
    }
    this.cancelScroll = restoreScrollPosition(
      this.restored,
      this.rootRef.current,
      30, // cards grow as their images decode
      settled
    );
  };

  scrollToCard = (target: HTMLElement) => {
    const container = getScrollContainer(this.rootRef.current);
    if (!container) {
      target.scrollIntoView();
      return;
    }
    // `.br-inner-container` has `scroll-behavior: smooth`, which would animate
    // the jump; an inline style beats the stylesheet rule.
    const prev = container.style.scrollBehavior;
    container.style.scrollBehavior = "auto";
    target.scrollIntoView();
    container.style.scrollBehavior = prev;
  };

  /**
   * Re-reads every page the user had already loaded and replaces the list, so
   * edits made on a detail page show up after a back-navigation.
   *
   * One request per page rather than one big one: the v2 API caps hitsPerPage
   * at 20 (Zod `.max(20)` in elementSevice), and exceeding it fails the parse
   * and returns a 500.
   */
  revalidateWindow = async () => {
    const pages = [];
    for (let i = 0; i <= this.state.page; i++) pages.push(i);
    const results = await Promise.all(
      pages.map((page) => this.fetchElementPage(page))
    ).catch(this.handleError);
    if (!results || !this.mounted) return;

    const elements: Array<any> = [];
    const seen: { [id: string]: boolean } = {};
    results.forEach((pageElements: any) => {
      (pageElements || []).forEach((element: any) => {
        // Offset paging can shift when items are added or removed between
        // visits, so the same element can come back on two pages.
        if (element && !seen[element.id]) {
          seen[element.id] = true;
          elements.push(element);
        }
      });
    });

    const lastPage = results[results.length - 1];
    this.setState({
      elements,
      hasMore: !!lastPage && lastPage.length >= this.state.batchSize,
    });
  };

  fetchElementPage = async (page: number) => {
    const { data } = await apiRequest(
      "api/elements/branch",
      "GET",
      this.elementParams(page),
    );
    return data.success ? data.data : null;
  };

  elementParams = (page: number) => ({
    targetUserId: this.context.selectedUserId,
    clubId: this.state.clubId,
    branchId: this.state.branchId,
    query: this.state.courseSearch,
    page,
    hitsPerPage: this.state.batchSize,
    showDeleted: this.state.showDeleted,
    showPast: this.state.showPast,
    tag:
      this.state.selectedTag !== "subscriptions" ? this.state.selectedTag : "",
    onlySubscriptions: this.state.selectedTag === "subscriptions",
  });

  requestElements = async () => {
    await apiRequest(
      "api/elements/branch",
      "GET",
      this.elementParams(this.state.page),
    )
      .then(this.handleElementsSuccess)
      .catch(this.handleError);
  };

  handleElementsSuccess = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ elementsLoaded: true });
    if (data.success) {
      const elements = this.state.elements.concat(data.data);
      this.setState(
        {
          elements,
          hasMore: data.data.length >= this.state.batchSize,
        },
        () => {
          if (!this.state.initialLoad) {
            // `location.hash` rather than slicing the full href - the old
            // version handed getElementById the entire URL whenever there was
            // no "#" at all.
            const highlighedCourseId = window.location.hash.slice(1);
            const element = highlighedCourseId
              ? document.getElementById(highlighedCourseId)
              : null;
            if (element) this.scrollToCard(element);
            this.setState({ initialLoad: true });
          }
        },
      );
    }
  };

  getNextPage = () => {
    // The restore pass writes scrollTop programmatically, which fires scroll
    // events. Without this guard InfiniteScroll would append a page - and
    // flash skeletons - before the restored list has settled.
    if (this.restoringPosition) return;
    this.setState(
      { page: this.state.page + 1, elementsLoaded: false },
      this.requestElements,
    );
  };

  requestBranch = async () => {
    await fetchRetry(
      "getBranchFromIdF",
      {
        clubId: this.state.clubId,
        branchId: this.state.branchId,
        targetUserId: this.context.selectedUserId,
      },
      1,
      5,
    )
      .then(this.handleBranch)
      .catch(this.handleError);
  };

  handleBranch = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ branchLoaded: true });
    if (data.success) {
      const branch = data.data;
      this.setState({
        branch: branch,
      });
    }
  };

  handleError = (err: any) => {
    console.error(err);
  };

  handleChangeCourseSearch = (val: string) => {
    this.setState({ courseSearch: val }, this.debounceRequest);
  };

  debounceRequest = debounce(() => {
    this.newRequest();
  }, 500);

  newRequest = () => {
    this.setState(
      { elements: [], page: 0, elementsLoaded: false, hasMore: true },
      this.requestElements,
    );
  };

  toggleShowDeleted = () => {
    this.setState({ showDeleted: !this.state.showDeleted }, this.newRequest);
  };

  toggleShowPast = () => {
    this.setState({ showPast: !this.state.showPast }, this.newRequest);
  };

  handleTagClicked = (tagId: string) => {
    if (tagId === this.state.selectedTag) {
      this.setState({ selectedTag: "" }, this.newRequest);
    } else {
      this.setState({ selectedTag: tagId }, this.newRequest);
    }
  };

  /**
   * The cards navigate through their own <Link>s, so there is no single
   * redirect handler to hook. A capture-phase listener on the list container
   * catches all of them without touching the card components - and ignores
   * sidebar navigation, which happens outside this subtree.
   */
  handleListClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target && target.closest && target.closest("a")) {
      this.leavingToDetail = true;
    }
  };

  render() {
    const tags = this.state.branch?.tags ? this.state.branch.tags : {};
    const tagArr = objectToArray(tags);
    tagArr.sort((a: any, b: any) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );

    const testMode = this.state.branch?.billing?.mode === "test";
    return (
      <>
        <div
          className="subscreen-main-container"
          ref={this.rootRef}
          onClickCapture={this.handleListClick}
        >
          <div className="subscreen-box-container">
            <Title title="Kurse" />
            <TestMode active={testMode} />
            {(this.state.branch?.user?.role === "Owner" ||
              this.context.isAdmin) && (
              <>
                <div className="inline-block">
                  <Link
                    className="create-btn"
                    to={`/club/${this.state.clubId}/branch/${this.state.branchId}/createCourse`}
                  >
                    <div className="create-icon-container">
                      <FontAwesomeIcon icon={faPlusCircle} size="1x" />
                    </div>
                    Kurs erstellen
                  </Link>
                </div>
                {this.state.branch?.settings?.useSubscriptions && (
                  <div className="inline-block">
                    <Link
                      className="create-btn"
                      to={`/club/${this.state.clubId}/branch/${this.state.branchId}/createSubscription`}
                    >
                      <div className="create-icon-container">
                        <FontAwesomeIcon icon={faPlusCircle} size="1x" />
                      </div>
                      Mitgliedschaft erstellen
                    </Link>
                  </div>
                )}
              </>
            )}
            <SearchInput
              name="course-search-input"
              placeholder="Kurssuche"
              value={this.state.courseSearch}
              onChange={this.handleChangeCourseSearch}
              className="no-margin"
            />
            <div className="tag-rating-container">
              {this.state.branch?.settings?.useSubscriptions && (
                <Tag
                  item={"Mitgliedschaften"}
                  selected={this.state.selectedTag === "subscriptions"}
                  color={"#c31924"}
                  onClick={() => {
                    this.handleTagClicked("subscriptions");
                  }}
                />
              )}
              {tagArr.map((currTag: any) => (
                <>
                  <Tag
                    item={currTag.name}
                    selected={currTag.id === this.state.selectedTag}
                    color={"#5DADE2"}
                    onClick={() => {
                      this.handleTagClicked(currTag.id);
                    }}
                  />
                </>
              ))}
            </div>
            <div>
              <TextCheckbox
                text="gelöschte Kurse anzeigen"
                val={this.state.showDeleted}
                onChange={this.toggleShowDeleted}
              />
              <TextCheckbox
                text="vergangene Kurse anzeigen"
                val={this.state.showPast}
                onChange={this.toggleShowPast}
              />
            </div>

            <>
              <InfiniteScroll
                dataLength={this.state.elements.length}
                next={this.getNextPage}
                hasMore={this.state.hasMore}
                loader={<></>}
                scrollableTarget="scroll-container"
              >
                {this.state.elements.map((element: any) => (
                  <>
                    {element.courseType === "single-course" && (
                      <SingleCourseElementManage
                        clubId={this.state.clubId}
                        branchId={this.state.branchId}
                        courseId={element.id}
                        title={element.title}
                        description={element.description}
                        startTime={element.startTime}
                        endTime={element.endTime}
                        where={element.location.name}
                        freePlaces={
                          testMode ? element.freePlacesTest : element.freePlaces
                        }
                        price={priceToStr(element.price)}
                        imgUrl={element.imgLowRes}
                        editable={
                          this.state.branch?.user?.role === "Owner" ||
                          this.context.isAdmin
                        }
                        viewable={this.state.branch?.user?.role === "ViewOnly"}
                        deleted={element.deleted}
                        key={element.id}
                        internalNote={element.internalNote}
                      />
                    )}
                    {element.courseType === "block-course" && (
                      <BlockCourseElementManage
                        clubId={this.state.clubId}
                        branchId={this.state.branchId}
                        courseId={element.id}
                        title={element.title}
                        description={element.description}
                        when={dateToFullStr(
                          firebaseDateToDateObj(element.startTime),
                        )}
                        courses={element.courses}
                        freePlaces={
                          testMode ? element.freePlacesTest : element.freePlaces
                        }
                        price={priceToStr(element.price)}
                        imgUrl={element.imgLowRes}
                        editable={
                          this.state.branch?.user?.role === "Owner" ||
                          this.context.isAdmin
                        }
                        viewable={this.state.branch?.user?.role === "ViewOnly"}
                        deleted={element.deleted}
                        key={element.id}
                        internalNote={element.internalNote}
                      />
                    )}
                    {element.courseType === "infinity-course" && (
                      <InfinityCourseElementManage
                        clubId={this.state.clubId}
                        branchId={this.state.branchId}
                        courseId={element.id}
                        title={element.title}
                        description={element.description}
                        where={element.location?.name}
                        time={element.time}
                        duration={element.duration}
                        weekDay={element.weekDay}
                        freePlaces={
                          testMode ? element.freePlacesTest : element.freePlaces
                        }
                        price={priceToStr(element.price)}
                        imgUrl={element.imgLowRes}
                        editable={
                          this.state.branch?.user?.role === "Owner" ||
                          this.context.isAdmin
                        }
                        viewable={this.state.branch?.user?.role === "ViewOnly"}
                        deleted={element.deleted}
                        key={element.id}
                        internalNote={element.internalNote}
                      />
                    )}
                    {element.courseType === "subscription" && (
                      <SubscriptionElementManage
                        clubId={this.state.clubId}
                        branchId={this.state.branchId}
                        subscriptionId={element.id}
                        title={element.title}
                        description={element.description}
                        price={priceToStr(element.price)}
                        imgUrl={element.imgLowRes}
                        editable={
                          this.state.branch?.user?.role === "Owner" ||
                          this.context.isAdmin
                        }
                        viewable={this.state.branch?.user?.role === "ViewOnly"}
                        deleted={element.deleted}
                        key={element.id}
                      />
                    )}
                  </>
                ))}
              </InfiniteScroll>
              {!this.state.elementsLoaded && (
                <>
                  <CourseElementSkeleton />
                  <CourseElementSkeleton />
                  <CourseElementSkeleton />
                  <CourseElementSkeleton />
                </>
              )}
              {this.state.elementsLoaded && this.state.hasMore && (
                <div className="load-more-container" onClick={this.getNextPage}>
                  Mehr laden
                </div>
              )}
            </>
          </div>
        </div>
      </>
    );
  }
}
